import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  DbEnums,
  TenantUserLifecycleStatusTypesEnum,
} from '@eluve/graphql-types';

const c = initContract();

const { CREATED, ONBOARDED, ACTIVE, CHURNED } =
  DbEnums.TenantUserLifecycleStatus;

export const tenantUserLifecycleStatusSchema = z.nativeEnum(
  DbEnums.TenantUserLifecycleStatus,
);

/**
 * Authoritative manual (tenant-admin / eluve-admin) lifecycle transition table,
 * shared by the api-nest enforcement and the web affordance map so the two
 * can't drift. Any pair not listed — including a same-state no-op — is rejected.
 * ACTIVE is never a manual target: it is sweep-only.
 */
export const MANUAL_LIFECYCLE_TRANSITIONS: Record<
  TenantUserLifecycleStatusTypesEnum,
  TenantUserLifecycleStatusTypesEnum[]
> = {
  [CREATED]: [ONBOARDED, CHURNED],
  [ONBOARDED]: [CREATED, CHURNED],
  [ACTIVE]: [CREATED, ONBOARDED, CHURNED],
  [CHURNED]: [CREATED, ONBOARDED],
};

const setUserLifecycleStatusBodySchema = z.object({
  // The full enum is accepted so the business-rule rejections (e.g. a manual
  // request for ACTIVE) surface as a typed transition error rather than a
  // schema 400 — see TenantUserLifecycleService.
  status: tenantUserLifecycleStatusSchema,
  // Send the rendered status as an optimistic-concurrency precondition
  // so we don't apply a decision made against stale state
  expectedStatus: tenantUserLifecycleStatusSchema.optional(),
});

const setUserLifecycleStatusResponseSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  lifecycleStatus: tenantUserLifecycleStatusSchema,
  statusChangedAt: z.string(),
  statusChangedBy: z.string().uuid().nullable(),
});

export type SetUserLifecycleStatusBody = z.infer<
  typeof setUserLifecycleStatusBodySchema
>;
export type SetUserLifecycleStatusResponse = z.infer<
  typeof setUserLifecycleStatusResponseSchema
>;

export const tenantUserLifecycleContract = c.router(
  {
    setUserLifecycleStatus: {
      method: 'POST',
      path: '/:userId/lifecycle-status',
      responses: {
        200: setUserLifecycleStatusResponseSchema,
      },
      body: setUserLifecycleStatusBodySchema,
      summary:
        'Set a tenant user lifecycle status with hard transition enforcement. Path-scoped to :tenantId so the global TenantGuard enforces tenant membership; RolesGuard requires tenant-admin. An eluve-admin acts cross-tenant by first provisioning into the tenant (which grants a tenant-admin row), so no eluve-admin special-case is needed. The single write path for tenant_users.lifecycle_status (there is deliberately no Hasura update permission).',
    },
  },
  {
    pathPrefix: '/:tenantId/users',
  },
);
